import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase"; // auth import edildi
import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { updateStreak, checkAndAwardBadge } from "../utils/streakUtils";

import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import "./GoalsAndJournalPage.css";

function GoalsAndJournalPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null); // userId state'e taşındı
  const username = localStorage.getItem("username") || "Misafir";
  const canvasRef = useRef(null);

  // State'ler
  const [goals, setGoals] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("Günlük");
  const [congrats, setCongrats] = useState("");
  const [allCompletedCongrats, setAllCompletedCongrats] = useState("");
  const [planCongrats, setPlanCongrats] = useState("");
  const [newGoal, setNewGoal] = useState("");
  const [goalPlan, setGoalPlan] = useState({ note: "", studyTime: "", analysis: "", date: "" });
  const [savedPlans, setSavedPlans] = useState([]);
  const [selectedPlanDate, setSelectedPlanDate] = useState(null);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [journalEntry, setJournalEntry] = useState("");
  const [journalTags, setJournalTags] = useState("");
  const [journalEntries, setJournalEntries] = useState([]);
  const [selectedJournalDate, setSelectedJournalDate] = useState(null);
  const [editingJournalId, setEditingJournalId] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showPlanHistoryModal, setShowPlanHistoryModal] = useState(false);
  const [showJournalHistoryModal, setShowJournalHistoryModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [summaryData, setSummaryData] = useState({
    completedGoals: 0,
    totalGoals: 0,
    studyTime: 0,
    streak: "0/4",
  });
  const [summaryError, setSummaryError] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");

  const kategoriler = ["Günlük", "Haftalık", "Aylık", "Uzun Vadeli"];

  // Kullanıcı doğrulama ve userId alma
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Mevcut Dökümanlara timestamp Ekleme (Bir kez çalışır)
  const addTimestampToExistingGoals = async () => {
    if (!userId) return;

    const hasMigrated = localStorage.getItem("goalsTimestampMigrated");
    if (hasMigrated === "true") {
      return; // Zaten çalışmışsa, fonksiyonu durdur
    }

    try {
      const goalsQuery = query(collection(db, "goals"), where("userId", "==", userId));
      const goalsSnapshot = await getDocs(goalsQuery);
      const updates = [];

      if (goalsSnapshot.empty) {
        localStorage.setItem("goalsTimestampMigrated", "true");
        return; // Hedef yoksa, çık
      }

      goalsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (!data.timestamp) {
          updates.push(
            setDoc(doc(db, "goals", docSnap.id), {
              ...data,
              timestamp: new Date(),
            })
          );
        }
      });

      if (updates.length > 0) {
        await Promise.all(updates);
        toast.info("Hedeflere tarih bilgisi eklendi.", { toastId: "timestampMigration" });
      }

      localStorage.setItem("goalsTimestampMigrated", "true");
    } catch (err) {
      console.error("Timestamp eklenirken hata:", err);
      setError("Hedeflere tarih bilgisi eklenirken hata oluştu.");
      toast.error("Hedeflere tarih bilgisi eklenirken hata oluştu.", { toastId: "timestampMigrationError" });
    }
  };

  // Veri Çekme
  useEffect(() => {
    if (!userId) return;

    const fetchGoals = async () => {
      try {
        const q = query(collection(db, "goals"), where("userId", "==", userId));
        const snapshot = await getDocs(q);
        const all = snapshot.empty
          ? []
          : snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setGoals(all);
      } catch (err) {
        setError(`Hedefler çekilirken hata oluştu: ${err.message}`);
        toast.error(`Hedefler çekilirken hata oluştu: ${err.message}`);
        setGoals([]);
      }
    };

    const fetchJournalEntries = async () => {
      try {
        const q = query(collection(db, "journalEntries"), where("userId", "==", userId));
        const snapshot = await getDocs(q);
        const entries = snapshot.empty
          ? []
          : snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setJournalEntries(entries);
      } catch (err) {
        setError(`Günlük girişleri çekilirken hata oluştu: ${err.message}`);
        toast.error(`Günlük girişleri çekilirken hata oluştu: ${err.message}`);
        setJournalEntries([]);
      }
    };

    const fetchPlans = async () => {
      try {
        const q = query(collection(db, "goalPlans"), where("userId", "==", userId));
        const snapshot = await getDocs(q);
        const plans = snapshot.empty
          ? []
          : snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setSavedPlans(plans);
      } catch (err) {
        setError(`Planlar çekilirken hata oluştu: ${err.message}`);
        toast.error(`Planlar çekilirken hata oluştu: ${err.message}`);
        setSavedPlans([]);
      }
    };

    const initialize = async () => {
      await addTimestampToExistingGoals();
      await fetchGoals();
      await fetchJournalEntries();
      await fetchPlans();
    };

    initialize();
  }, [userId]);

  // Tüm Hedefler Tamamlandı Kontrolü
  useEffect(() => {
    const filteredGoals = goals.filter((g) => g.category === selectedCategory);
    const allCompleted = filteredGoals.length > 0 && filteredGoals.every((g) => g.done);
    if (allCompleted) {
      setAllCompletedCongrats("Tüm hedefler tamamlandı! Müthiş bir iş çıkardın! 🎉");
      setTimeout(() => setAllCompletedCongrats(""), 5000);
    } else {
      setAllCompletedCongrats("");
    }
  }, [goals, selectedCategory]);

  // Plan Kaydetme ve Güncelleme
  const savePlan = async () => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return;
    }

    try {
      const isEditing = !!editingPlanId;
      const docId = isEditing ? editingPlanId : `${userId}_${selectedCategory}_${goalPlan.date || new Date().toISOString().split("T")[0]}`;
      const data = {
        ...goalPlan,
        userId,
        category: selectedCategory,
        timestamp: new Date(),
        completed: isEditing ? savedPlans.find((p) => p.id === editingPlanId).completed : false,
      };

      await setDoc(doc(db, "goalPlans", docId), data);
      if (isEditing) {
        setSavedPlans((prev) => prev.map((p) => (p.id === docId ? { ...data, id: docId } : p)));
        setEditingPlanId(null);
        toast.success("Plan güncellendi!");
      } else {
        setSavedPlans((prev) => [...prev, { ...data, id: docId }]);
        toast.success("Plan kaydedildi!");
      }
      setGoalPlan({ note: "", studyTime: "", analysis: "", date: "" });
    } catch (err) {
      setError(`Plan kaydedilirken hata oluştu: ${err.message}`);
      toast.error(`Plan kaydedilirken hata oluştu: ${err.message}`);
    }
  };

  // Plan Düzenleme
  const editPlan = (plan) => {
    setEditingPlanId(plan.id);
    setGoalPlan({
      note: plan.note,
      studyTime: plan.studyTime,
      analysis: plan.analysis,
      date: plan.date,
    });
    setSelectedPlanDate(plan.date);
  };

  // Plan Tamamlama
  const completePlan = async (planId) => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return;
    }

    try {
      const planRef = doc(db, "goalPlans", planId);
      const planDoc = await getDoc(planRef);
      if (!planDoc.exists()) {
        throw new Error("Plan bulunamadı.");
      }
      const plan = planDoc.data();
      if (plan.userId !== userId) {
        throw new Error("Bu planı tamamlama yetkiniz yok.");
      }

      await setDoc(planRef, { ...plan, completed: true });
      setSavedPlans((prev) => prev.map((p) => (p.id === planId ? { ...p, completed: true } : p)));
      await updateStreak(userId, "plan");
      await checkAndAwardBadge(userId, "plan");
      setPlanCongrats("Plan tamamlandı! Harika iş! 🌟");
      setTimeout(() => setPlanCongrats(""), 3000);
      toast.success("Plan tamamlandı!");
    } catch (err) {
      setError(`Plan tamamlanırken hata oluştu: ${err.message}`);
      toast.error(`Plan tamamlanırken hata oluştu: ${err.message}`);
    }
  };

  // Plan Silme
  const deletePlan = async (planId) => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return;
    }

    try {
      const planRef = doc(db, "goalPlans", planId);
      const planDoc = await getDoc(planRef);
      if (!planDoc.exists()) {
        throw new Error("Plan bulunamadı.");
      }
      const plan = planDoc.data();
      if (plan.userId !== userId) {
        throw new Error("Bu planı silme yetkiniz yok.");
      }

      await deleteDoc(planRef);
      setSavedPlans((prev) => prev.filter((p) => p.id !== planId));
      if (selectedPlanDate === planId.split("_")[2]) setSelectedPlanDate(null);
      if (editingPlanId === planId) setEditingPlanId(null);
      toast.success("Plan silindi!");
    } catch (err) {
      setError(`Plan silinirken hata oluştu: ${err.message}`);
      toast.error(`Plan silinirken hata oluştu: ${err.message}`);
    }
  };

  // Hedef Kaydetme
  const saveGoalToDB = async (goal) => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return null;
    }

    try {
      const ref = doc(collection(db, "goals"));
      const goalWithTimestamp = {
        ...goal,
        userId,
        timestamp: new Date(),
      };
      await setDoc(ref, goalWithTimestamp);
      toast.success("Hedef kaydedildi!");
      return { ...goalWithTimestamp, id: ref.id };
    } catch (err) {
      setError(`Hedef kaydedilirken hata oluştu: ${err.message}`);
      toast.error(`Hedef kaydedilirken hata oluştu: ${err.message}`);
      return null;
    }
  };

  // Hedef Güncelleme
  const updateGoalInDB = async (goalId, updatedGoal) => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return;
    }

    try {
      const ref = doc(db, "goals", goalId);
      const goalDoc = await getDoc(ref);
      if (!goalDoc.exists()) {
        throw new Error("Hedef bulunamadı.");
      }
      const goal = goalDoc.data();
      if (goal.userId !== userId) {
        throw new Error("Bu hedefi güncelleme yetkiniz yok.");
      }

      await setDoc(ref, updatedGoal);
      toast.success("Hedef güncellendi!");
    } catch (err) {
      setError(`Hedef güncellenirken hata oluştu: ${err.message}`);
      toast.error(`Hedef güncellenirken hata oluştu: ${err.message}`);
    }
  };

  // Hedef Silme
  const deleteGoalFromDB = async (goalId) => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return;
    }

    try {
      const ref = doc(db, "goals", goalId);
      const goalDoc = await getDoc(ref);
      if (!goalDoc.exists()) {
        throw new Error("Hedef bulunamadı.");
      }
      const goal = goalDoc.data();
      if (goal.userId !== userId) {
        throw new Error("Bu hedefi silme yetkiniz yok.");
      }

      await deleteDoc(ref);
      toast.success("Hedef silindi!");
    } catch (err) {
      setError(`Hedef silinirken hata oluştu: ${err.message}`);
      toast.error(`Hedef silinirken hata oluştu: ${err.message}`);
    }
  };

  // Hedef Ekleme
  const addGoal = async () => {
    if (!newGoal.trim()) {
      setError("Hedef boş olamaz!");
      toast.error("Hedef boş olamaz!");
      return;
    }
    const goal = {
      text: newGoal,
      category: selectedCategory,
      done: false,
      progress: 0,
      timestamp: new Date(),
    };
    const saved = await saveGoalToDB(goal);
    if (saved) {
      setGoals([...goals, saved]);
      setNewGoal("");
      setCongrats("");
    }
  };

  // Hedef Durumunu Değiştirme
  const toggleGoal = async (goal) => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return;
    }

    const updated = { ...goal, done: !goal.done, progress: !goal.done ? 100 : 0 };
    await updateGoalInDB(goal.id, updated);
    setGoals((prev) => prev.map((g) => (g.id === goal.id ? updated : g)));
    if (updated.done) {
      setCongrats("Hedef tamamlandı! Harikasın! 🎉");
      setTimeout(() => setCongrats(""), 3000);
      try {
        await updateStreak(userId, "goal");
        await checkAndAwardBadge(userId, "goal");
        toast.success("Hedef tamamlandı!");
      } catch (err) {
        setError(`Streak veya rozet güncellenirken hata oluştu: ${err.message}`);
        toast.error(`Streak veya rozet güncellenirken hata oluştu: ${err.message}`);
      }
    } else {
      setCongrats("");
      toast.info("Hedef tamamlanmamış olarak işaretlendi.");
    }
  };

  // Hedef Silme
  const deleteGoal = async (goal) => {
    await deleteGoalFromDB(goal.id);
    setGoals((prev) => prev.filter((g) => g.id !== goal.id));
  };

  // Günlük Ekleme ve Güncelleme
  const addJournalEntry = async () => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return;
    }

    if (!journalEntry.trim()) {
      setError("Günlük girişi boş olamaz!");
      toast.error("Günlük girişi boş olamaz!");
      return;
    }
    try {
      if (editingJournalId) {
        const entryRef = doc(db, "journalEntries", editingJournalId);
        const entryDoc = await getDoc(entryRef);
        if (!entryDoc.exists()) {
          throw new Error("Günlük girişi bulunamadı.");
        }
        const entryData = entryDoc.data();
        if (entryData.userId !== userId) {
          throw new Error("Bu günlük girişini güncelleme yetkiniz yok.");
        }

        const entry = {
          userId,
          date: selectedJournalDate || new Date().toISOString().split("T")[0],
          entry: journalEntry,
          tags: journalTags.split(",").map((tag) => tag.trim()).filter((tag) => tag),
          timestamp: new Date().toISOString(),
        };
        await updateDoc(entryRef, entry);
        setJournalEntries((prev) =>
          prev.map((e) => (e.id === editingJournalId ? { ...entry, id: editingJournalId } : e))
        );
        setEditingJournalId(null);
        toast.success("Günlük güncellendi!");
      } else {
        const entry = {
          userId,
          date: new Date().toISOString().split("T")[0],
          entry: journalEntry,
          tags: journalTags.split(",").map((tag) => tag.trim()).filter((tag) => tag),
          timestamp: new Date().toISOString(),
        };
        const ref = await addDoc(collection(db, "journalEntries"), entry);
        setJournalEntries([...journalEntries, { ...entry, id: ref.id }]);
        toast.success("Günlük girişi kaydedildi!");
      }
      setJournalEntry("");
      setJournalTags("");
    } catch (err) {
      setError(`Günlük girişi kaydedilirken hata oluştu: ${err.message}`);
      toast.error(`Günlük girişi kaydedilirken hata oluştu: ${err.message}`);
    }
  };

  // Günlük Düzenleme
  const editJournalEntry = (entry) => {
    setEditingJournalId(entry.id);
    setJournalEntry(entry.entry);
    setJournalTags(entry.tags.join(", "));
    setSelectedJournalDate(entry.date);
  };

  // Günlük Silme
  const deleteJournalEntry = async (entryId) => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return;
    }

    try {
      const entryRef = doc(db, "journalEntries", entryId);
      const entryDoc = await getDoc(entryRef);
      if (!entryDoc.exists()) {
        throw new Error("Günlük girişi bulunamadı.");
      }
      const entry = entryDoc.data();
      if (entry.userId !== userId) {
        throw new Error("Bu günlük girişini silme yetkiniz yok.");
      }

      await deleteDoc(entryRef);
      setJournalEntries((prev) => prev.filter((e) => e.id !== entryId));
      if (editingJournalId === entryId) setEditingJournalId(null);
      toast.success("Günlük girişi silindi!");
    } catch (err) {
      setError(`Günlük girişi silinirken hata oluştu: ${err.message}`);
      toast.error(`Günlük girişi silinirken hata oluştu: ${err.message}`);
    }
  };

  // Günün Özeti Verilerini Çekme
  const fetchSummaryData = async () => {
    if (!userId) {
      toast.error("Kullanıcı kimliği bulunamadı.");
      return;
    }

    setIsGenerating(true);
    setSummaryError("");
    setImageUrl(null);

    try {
      const startOfDay = new Date(selectedDate + "T00:00:00Z");
      const endOfDay = new Date(selectedDate + "T23:59:59Z");

      let completedGoals = 0;
      let totalGoals = 0;
      try {
        const goalsQuery = query(
          collection(db, "goals"),
          where("userId", "==", userId),
          where("category", "==", "Günlük"),
          where("timestamp", ">=", startOfDay),
          where("timestamp", "<=", endOfDay)
        );
        const goalsSnapshot = await getDocs(goalsQuery);

        if (goalsSnapshot.empty) {
          console.log("Seçilen tarihe ait hedef verisi bulunamadı, varsayılan değerler atanıyor.");
          completedGoals = 0;
          totalGoals = 0;
        } else {
          const goalsData = goalsSnapshot.docs.map((doc) => doc.data());
          completedGoals = goalsData.filter((g) => g.done === true).length;
          totalGoals = goalsData.length;
          console.log("Hedef verileri:", { completedGoals, totalGoals, goalsData });
        }
      } catch (err) {
        console.error("Hedef verileri çekilirken hata:", err);
        setSummaryError("Hedef verileri çekilirken bir hata oluştu.");
        toast.error("Hedef verileri çekilirken hata oluştu.");
        completedGoals = 0;
        totalGoals = 0;
      }

      let totalStudyTime = 0;
      try {
        const studyQuery = query(
          collection(db, "studyEntries"),
          where("userId", "==", userId),
          where("date", "==", selectedDate)
        );
        const studySnapshot = await getDocs(studyQuery);
        totalStudyTime = studySnapshot.empty
          ? 0
          : studySnapshot.docs.reduce(
              (sum, doc) => sum + (doc.data().duration || 0),
              0
            );
      } catch (err) {
        console.error("Çalışma süresi verileri çekilirken hata:", err);
        toast.error("Çalışma süresi verileri çekilirken hata oluştu.");
        totalStudyTime = 0;
      }

      const streakTypes = ["goal", "plan", "journal", "task"];
      let completedStreaks = 0;

      if (completedGoals > 0) completedStreaks++;

      try {
        const planQuery = query(
          collection(db, "goalPlans"),
          where("userId", "==", userId),
          where("date", "==", selectedDate),
          where("completed", "==", true)
        );
        const planSnapshot = await getDocs(planQuery);
        if (!planSnapshot.empty) completedStreaks++;
      } catch (err) {
        console.error("Plan verileri çekilirken hata:", err);
        toast.error("Plan verileri çekilirken hata oluştu.");
      }

      try {
        const journalQuery = query(
          collection(db, "journalEntries"),
          where("userId", "==", userId),
          where("date", "==", selectedDate)
        );
        const journalSnapshot = await getDocs(journalQuery);
        if (!journalSnapshot.empty) completedStreaks++;
      } catch (err) {
        console.error("Günlük verileri çekilirken hata:", err);
        toast.error("Günlük verileri çekilirken hata oluştu.");
      }

      try {
        const tasksQuery = query(
          collection(db, "dailyTasks"),
          where("userId", "==", userId),
          where("date", "==", selectedDate)
        );
        const tasksSnapshot = await getDocs(tasksQuery);
        if (!tasksSnapshot.empty && tasksSnapshot.docs[0].data().tasks) {
          const tasksCompleted = tasksSnapshot.docs[0].data().tasks.filter((t) => t.completed).length;
          if (tasksCompleted > 0) completedStreaks++;
        }
      } catch (err) {
        console.error("Görev verileri çekilirken hata:", err);
        toast.error("Görev verileri çekilirken hata oluştu.");
      }

      const streakDisplay = `${completedStreaks}/${streakTypes.length}`;

      const summary = {
        completedGoals,
        totalGoals,
        studyTime: totalStudyTime,
        streak: streakDisplay,
      };

      setSummaryData(summary);
      generateSummaryCard(summary);
      toast.success("Günün özeti kartı oluşturuldu!");
    } catch (err) {
      setSummaryError(
        "Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin veya internet bağlantınızı kontrol edin."
      );
      toast.error("Günün özeti verileri çekilirken hata oluştu.");
      console.error("Günün özeti verileri çekilirken hata:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Günün Özeti Kartını Oluşturma
  const generateSummaryCard = (data) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = 500;
    canvas.height = 350;

    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, "#1e3a8a");
    gradient.addColorStop(1, "#3b82f6");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "rgba(255, 255, 255, 0.1)";
    ctx.beginPath();
    ctx.moveTo(0, 50);
    ctx.bezierCurveTo(150, 0, 350, 100, 500, 50);
    ctx.lineTo(500, 0);
    ctx.lineTo(0, 0);
    ctx.closePath();
    ctx.fill();

    const glowGradient = ctx.createRadialGradient(250, 175, 0, 250, 175, 300);
    glowGradient.addColorStop(0, "rgba(255, 255, 255, 0.2)");
    glowGradient.addColorStop(1, "rgba(255, 255, 255, 0)");
    ctx.fillStyle = glowGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.font = "bold 28px 'Poppins', 'Inter', sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.shadowColor = "rgba(255, 255, 255, 0.5)";
    ctx.shadowBlur = 10;
    ctx.fillText(`${username}'ın Günlük Özeti`, canvas.width / 2, 40);
    ctx.shadowBlur = 0;

    ctx.font = "14px 'Inter', sans-serif";
    ctx.fillStyle = "#d1d5db";
    ctx.fillText(selectedDate, canvas.width / 2, 60);

    const lineGradient = ctx.createLinearGradient(20, 0, canvas.width - 20, 0);
    lineGradient.addColorStop(0, "transparent");
    lineGradient.addColorStop(0.5, "#60a5fa");
    lineGradient.addColorStop(1, "transparent");
    ctx.strokeStyle = lineGradient;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(20, 80);
    ctx.lineTo(canvas.width - 20, 80);
    ctx.stroke();

    const stats = [
      { label: "Tamamlanan Hedefler", value: `${data.completedGoals}/${data.totalGoals}`, icon: "🎯" },
      { label: "Çalışma Süresi", value: `${Math.floor(data.studyTime / 3600)} sa ${Math.floor((data.studyTime % 3600) / 60)} dk`, icon: "⏰" },
      { label: "Streak", value: data.streak, icon: "🔥" },
    ];

    stats.forEach((stat, index) => {
      const y = 110 + index * 60;
      const iconGradient = ctx.createLinearGradient(30, y - 20, 30, y);
      iconGradient.addColorStop(0, "#60a5fa");
      iconGradient.addColorStop(1, "#3b82f6");
      ctx.fillStyle = iconGradient;
      ctx.beginPath();
      ctx.arc(40, y - 10, 20, 0, Math.PI * 2);
      ctx.fill();

      ctx.font = "24px 'Inter', sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.fillText(stat.icon, 40, y);

      ctx.font = "bold 16px 'Inter', sans-serif";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "left";
      ctx.fillText(stat.label, 70, y);

      ctx.font = "16px 'Inter', sans-serif";
      ctx.fillStyle = "#60a5fa";
      ctx.textAlign = "right";
      ctx.fillText(stat.value, canvas.width - 30, y);
    });

    ctx.font = "italic 14px 'Poppins', 'Inter', sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.fillText("Başarılarını paylaş, ilham ver! ⛵", canvas.width / 2, canvas.height - 20);

    const dataUrl = canvas.toDataURL("image/png");
    setImageUrl(dataUrl);
  };

  // Kartı İndirme
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `gunun-ozeti-${selectedDate}.png`;
    link.click();
    toast.success("Özet kartı indirildi!");
  };

  // Filtreleme
  const filteredGoals = goals.filter((g) => g.category === selectedCategory);
  const progress = filteredGoals.length
    ? Math.round((filteredGoals.filter((g) => g.done).length / filteredGoals.length) * 100)
    : 0;

  // Tarihsel Plan ve Günlük Listesi
  const planDates = [...new Set(savedPlans.filter((p) => p.category === selectedCategory).map((p) => p.date))].sort().reverse();
  const journalDates = [...new Set(journalEntries.map((e) => e.date))].sort().reverse();

  const getCategoryDescription = (category) => {
    switch (category) {
      case "Günlük": return "Bugünün hedeflerini planla ve izle.";
      case "Haftalık": return "Haftalık hedeflerini belirle.";
      case "Aylık": return "Aylık planlarını oluştur.";
      case "Uzun Vadeli": return "Büyük hayallerini gerçeğe dönüştür.";
      default: return "";
    }
  };

  return (
    <motion.div
      className="goals-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="goals-background"></div>
      
      <div className="bento-dashboard">
        
        {/* SIDEBAR */}
        <div className="bento-sidebar">
          
          <div className="bento-card bento-profile-progress">
             <div className="progress-ring-container">
               <svg viewBox="0 0 36 36" className="circular-chart">
                 <path className="circle-bg"
                   d="M18 2.0845
                     a 15.9155 15.9155 0 0 1 0 31.831
                     a 15.9155 15.9155 0 0 1 0 -31.831"
                 />
                 <path className="circle"
                   strokeDasharray={`${progress}, 100`}
                   d="M18 2.0845
                     a 15.9155 15.9155 0 0 1 0 31.831
                     a 15.9155 15.9155 0 0 1 0 -31.831"
                   stroke={progress === 100 ? "#10b981" : "#6366f1"}
                 />
                 <text x="18" y="20.35" className="percentage">{progress}%</text>
               </svg>
               <div className="ring-subtitle">
                 {filteredGoals.filter(g => g.done).length} / {filteredGoals.length} Hedef Tamamlandı
               </div>
             </div>

             <button 
               className="action-button" 
               style={{ 
                 width: "100%", 
                 marginTop: "1.5rem",
                 background: "linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))",
                 border: "none",
                 padding: "12px",
                 borderRadius: "12px",
                 fontWeight: "600",
                 color: "#fff",
                 boxShadow: "0 10px 20px rgba(99, 102, 241, 0.3)",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 gap: "8px",
                 transition: "all 0.3s ease"
               }}
               onClick={() => setShowSummaryModal(true)}
               onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
               onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}
             >
               📸 Günün Özeti Kartı
             </button>
          </div>

          <div className="bento-card bento-categories">
            <div className="bento-card-header">
               <h3 className="bento-card-title">Kategoriler</h3>
            </div>
            <div className="category-list">
              {kategoriler.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setSelectedPlanDate(null);
                    setSelectedJournalDate(null);
                  }}
                  className={`category-pill ${selectedCategory === cat ? "active" : ""}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* MAIN CONTENT */}
        <div className="bento-main">
          
          {/* Banners */}
          <AnimatePresence>
            {congrats && <motion.div className="congrats-banner" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>{congrats}</motion.div>}
            {allCompletedCongrats && <motion.div className="congrats-banner" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>{allCompletedCongrats}</motion.div>}
            {planCongrats && <motion.div className="congrats-banner" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>{planCongrats}</motion.div>}
          </AnimatePresence>

          {/* Goals Card */}
          <div className="bento-card">
            <div className="bento-card-header">
               <h3 className="bento-card-title">🎯 {selectedCategory} Hedeflerin</h3>
               <p className="text-sm text-secondary" style={{margin:0}}>{getCategoryDescription(selectedCategory)}</p>
            </div>
            
            <div className="goals-grid">
              <AnimatePresence>
                {filteredGoals.length === 0 ? (
                  <motion.div style={{color: 'var(--text-secondary)'}} initial={{opacity:0}} animate={{opacity:1}}>Henüz hedef eklenmedi. Hadi bir tane ekle! 🚀</motion.div>
                ) : (
                  filteredGoals.map((g) => (
                    <motion.div
                      key={g.id}
                      className={`bento-goal-card ${g.done ? "completed" : ""}`}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                    >
                       <div className="goal-card-top">
                          <h4 className="goal-title">{g.text}</h4>
                          <input 
                             type="checkbox" 
                             className="goal-checkbox" 
                             checked={g.done}
                             onChange={() => toggleGoal(g)}
                          />
                       </div>
                       <div className="goal-card-bottom">
                          <button className="delete-icon-btn" onClick={() => deleteGoal(g)} title="Sil">
                            🗑️
                          </button>
                       </div>
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>

            <div className="inline-add-goal">
              <input
                type="text"
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addGoal()}
                className="add-goal-input"
                placeholder="Yeni hedef ekle..."
              />
              <button className="add-goal-btn" onClick={addGoal}>+</button>
            </div>
          </div>

          {/* Split Row for Plan & Journal */}
          <div className={`bento-split-row ${selectedCategory !== "Günlük" ? "single-col" : ""}`}>
            
            {/* Plan Card */}
            <div className="bento-card">
               <div className="bento-card-header">
                 <h3 className="bento-card-title">📝 {selectedCategory} Planlama</h3>
                 <button className="history-action" onClick={() => setShowPlanHistoryModal(true)}>
                    🕒 Geçmişi Gör
                 </button>
               </div>
               <div className="minimal-form">
                  <div className="minimal-input-group">
                    <label className="minimal-label">Tarih</label>
                    <input type="date" className="minimal-input" value={goalPlan.date} onChange={(e) => setGoalPlan({ ...goalPlan, date: e.target.value })} />
                  </div>
                  <div className="minimal-input-group">
                    <label className="minimal-label">Çalışma Süresi</label>
                    <input type="text" className="minimal-input" placeholder="örn: 2 saat" value={goalPlan.studyTime} onChange={(e) => setGoalPlan({ ...goalPlan, studyTime: e.target.value })} />
                  </div>
                  <div className="minimal-input-group">
                    <label className="minimal-label">Notların</label>
                    <textarea className="minimal-textarea" placeholder="Bugün neler yapacaksın?" value={goalPlan.note} onChange={(e) => setGoalPlan({ ...goalPlan, note: e.target.value })} />
                  </div>
                  <div className="minimal-input-group">
                    <label className="minimal-label">Analiz</label>
                    <textarea className="minimal-textarea" placeholder="Günün sonunda ne öğrendin?" value={goalPlan.analysis} onChange={(e) => setGoalPlan({ ...goalPlan, analysis: e.target.value })} />
                  </div>
                  <button className="action-button" style={{marginTop: "auto"}} onClick={savePlan}>
                    {editingPlanId ? "Güncelle" : "Kaydet"}
                  </button>
               </div>
            </div>

            {/* Journal Card (Only if Daily) */}
            {selectedCategory === "Günlük" && (
              <div className="bento-card">
                 <div className="bento-card-header">
                   <h3 className="bento-card-title">📓 Günlük</h3>
                   <button className="history-action" onClick={() => setShowJournalHistoryModal(true)}>
                      🕒 Geçmişi Gör
                   </button>
                 </div>
                 <div className="minimal-form">
                    <div className="minimal-input-group">
                      <label className="minimal-label">Bugün Neler Yaptın?</label>
                      <textarea className="minimal-textarea large" placeholder="İçinden geçenleri yaz..." value={journalEntry} onChange={(e) => setJournalEntry(e.target.value)} />
                    </div>
                    <div className="minimal-input-group">
                      <label className="minimal-label">Etiketler</label>
                      <input type="text" className="minimal-input" placeholder="mutlu, verimli" value={journalTags} onChange={(e) => setJournalTags(e.target.value)} />
                    </div>
                    <button className="action-button" style={{marginTop: "auto"}} onClick={addJournalEntry}>
                      {editingJournalId ? "Güncelle" : "Kaydet"}
                    </button>
                 </div>
              </div>
            )}

          </div>

        </div>
      </div>

      {/* --- MODALS --- */}
      
      {/* Plan History Modal */}
      <AnimatePresence>
        {showPlanHistoryModal && (
          <div className="modal-overlay" onClick={() => setShowPlanHistoryModal(false)}>
            <motion.div className="modal-content" onClick={e => e.stopPropagation()} initial={{scale:0.9, opacity:0}} animate={{scale:1, opacity:1}} exit={{scale:0.9, opacity:0}}>
              <button className="close-button" onClick={() => setShowPlanHistoryModal(false)}>✕</button>
              <h3 className="modal-title mb-4">📅 Planlama Geçmişi</h3>
              <div className="history-list">
                {savedPlans.filter(p => p.category === selectedCategory).length === 0 ? (
                  <p className="text-secondary">Bu kategoride hiç plan yok.</p>
                ) : (
                  savedPlans.filter(p => p.category === selectedCategory).map(plan => (
                    <div key={plan.id} className="history-card">
                      <div className="history-card-header">
                        <span>{plan.date}</span>
                        {plan.completed && <span className="text-success">✔ Tamamlandı</span>}
                      </div>
                      <div className="history-content">
                        <strong>Süre:</strong> {plan.studyTime} <br/>
                        <strong>Not:</strong> {plan.note} <br/>
                        <strong>Analiz:</strong> {plan.analysis}
                      </div>
                      <div className="history-actions">
                        {!plan.completed && <button className="history-action" onClick={() => {completePlan(plan.id); setShowPlanHistoryModal(false);}}>Tamamla</button>}
                        <button className="history-action" onClick={() => {editPlan(plan); setShowPlanHistoryModal(false);}}>Düzenle</button>
                        <button className="history-action" style={{color:'#ef4444'}} onClick={() => deletePlan(plan.id)}>Sil</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Journal History Modal */}
      <AnimatePresence>
        {showJournalHistoryModal && (
          <div className="modal-overlay" onClick={() => setShowJournalHistoryModal(false)}>
            <motion.div className="modal-content" onClick={e => e.stopPropagation()} initial={{scale:0.9, opacity:0}} animate={{scale:1, opacity:1}} exit={{scale:0.9, opacity:0}}>
              <button className="close-button" onClick={() => setShowJournalHistoryModal(false)}>✕</button>
              <h3 className="modal-title mb-4">📓 Günlük Geçmişi</h3>
              <div className="history-list">
                {journalEntries.length === 0 ? (
                  <p className="text-secondary">Henüz günlük girişi yok.</p>
                ) : (
                  journalEntries.map(entry => (
                    <div key={entry.id} className="history-card">
                      <div className="history-card-header">
                        <span>{entry.date}</span>
                        <span>{new Date(entry.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>
                      <div className="history-content">
                        {entry.entry}
                      </div>
                      <div className="text-sm text-secondary mb-2">
                        Etiketler: {entry.tags.length > 0 ? entry.tags.join(", ") : "Yok"}
                      </div>
                      <div className="history-actions">
                        <button className="history-action" onClick={() => {editJournalEntry(entry); setShowJournalHistoryModal(false);}}>Düzenle</button>
                        <button className="history-action" style={{color:'#ef4444'}} onClick={() => deleteJournalEntry(entry.id)}>Sil</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Günün Özeti Modal */}
      <AnimatePresence>
        {showSummaryModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSummaryModal(false)}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <h3 className="modal-title" style={{ fontSize: '1.5rem', marginBottom: '8px' }}>📸 Günün Özeti Kartı</h3>
              <p className="text-secondary text-sm mb-6 text-center">Hedeflerini, çalışma süreni ve serini tek bir görselde topla!</p>
              
              <div className="minimal-input-group mt-4 mb-4" style={{ background: 'var(--bg-tertiary)', padding: '16px', borderRadius: '16px', border: '1px solid var(--border-subtle)' }}>
                <label className="minimal-label" style={{ fontWeight: '600', marginBottom: '8px', display: 'block', color: 'var(--text-primary)' }}>Tarih Seçin</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="minimal-input"
                  max={new Date().toISOString().split("T")[0]}
                  style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}
                />
              </div>
              <button
                onClick={fetchSummaryData}
                className="action-button w-full"
                disabled={isGenerating}
                style={{ 
                  background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))', 
                  border: 'none', 
                  padding: '14px', 
                  borderRadius: '12px', 
                  fontWeight: '700', 
                  color: '#fff',
                  boxShadow: '0 8px 20px rgba(99, 102, 241, 0.4)'
                }}
              >
                {isGenerating ? "Kart Oluşturuluyor... ⏳" : "Kartı Oluştur ✨"}
              </button>
              {summaryError && <p className="error-text mt-4">{summaryError}</p>}
              <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
              {isGenerating ? (
                <div className="loading mt-6 text-center">
                  <div className="spinner" style={{ margin: '0 auto', width: '40px', height: '40px', borderTopColor: 'var(--accent-primary)' }}></div>
                  <p className="loading-text mt-3 text-secondary">Veriler derleniyor...</p>
                </div>
              ) : (
                imageUrl && (
                  <div className="mt-6" style={{ background: 'var(--bg-tertiary)', padding: '16px', borderRadius: '20px', border: '1px solid var(--border-subtle)' }}>
                    <img src={imageUrl} alt="Günün Özeti" className="w-full rounded-lg" style={{ boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }} />
                    <button
                      onClick={handleDownload}
                      className="action-button w-full mt-4"
                      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', padding: '12px', borderRadius: '10px', fontWeight: '600' }}
                    >
                      📥 Cihaza İndir ve Paylaş
                    </button>
                  </div>
                )
              )}
              <button
                onClick={() => setShowSummaryModal(false)}
                className="close-button"
                style={{ top: '16px', right: '16px' }}
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
          <motion.p
            className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-red-500/20 text-red-400 px-4 py-2 rounded-lg backdrop-blur-md border border-red-500/30"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default GoalsAndJournalPage;
